import { DOMParser } from '@xmldom/xmldom';
import { logError, logInfo } from '../logger';
import { calculateRouteDistance } from '../routeHelpers';

export const parseKML = async (content) => {
  try {
    const parser = new DOMParser();
    const kml = parser.parseFromString(content, 'text/xml');
    const points = [];
    const routes = [];
    const layers = new Set();

    // Parse Placemarks
    const placemarks = kml.getElementsByTagName('Placemark');
    for (let i = 0; i < placemarks.length; i++) {
      const placemark = placemarks[i];
      const name = placemark.getElementsByTagName('name')[0]?.textContent;
      const description = placemark.getElementsByTagName('description')[0]?.textContent;
      
      // Try to determine layer/folder
      let layer = 'default';
      let parentNode = placemark.parentNode;
      while (parentNode && parentNode.nodeName !== '#document') {
        if (parentNode.nodeName === 'Folder' || parentNode.nodeName === 'Document') {
          const folderName = parentNode.getElementsByTagName('name')[0]?.textContent;
          if (folderName) {
            layer = folderName;
            break;
          }
        }
        parentNode = parentNode.parentNode;
      }
      
      // Add layer to set
      layers.add(layer);

      // Handle Points
      const point = placemark.getElementsByTagName('Point')[0];
      if (point) {
        const coords = point.getElementsByTagName('coordinates')[0]?.textContent;
        if (coords) {
          const [longitude, latitude] = coords.split(',').map(Number);
          points.push({
            type: 'Custom',
            coordinates: { latitude, longitude },
            notes: description || '',
            properties: { name },
            layer
          });
        }
      }

      // Handle LineStrings (Routes)
      const lineString = placemark.getElementsByTagName('LineString')[0];
      if (lineString) {
        const coords = lineString.getElementsByTagName('coordinates')[0]?.textContent;
        if (coords) {
          const trackPoints = coords.trim().split(/\s+/).map(coord => {
            const [longitude, latitude] = coord.split(',').map(Number);
            return {
              latitude,
              longitude,
              timestamp: new Date().toISOString()
            };
          });

          // Calculate route distance if we have at least 2 points
          const distance = trackPoints.length >= 2 ? calculateRouteDistance(trackPoints) : 0;

          routes.push({
            name: name || `Imported Route ${routes.length + 1}`,
            description: description || '',
            trackPoints,
            distance,
            color: '#3B82F6', // Default blue color
            properties: {
              source: 'kml',
              type: 'route',
              layer
            }
          });
        }
      }
      
      // Handle Polygons (not currently supported, but could be converted to routes)
      const polygon = placemark.getElementsByTagName('Polygon')[0];
      if (polygon) {
        const outerBoundary = polygon.getElementsByTagName('outerBoundaryIs')[0];
        if (outerBoundary) {
          const linearRing = outerBoundary.getElementsByTagName('LinearRing')[0];
          if (linearRing) {
            const coords = linearRing.getElementsByTagName('coordinates')[0]?.textContent;
            if (coords) {
              const trackPoints = coords.trim().split(/\s+/).map(coord => {
                const [longitude, latitude] = coord.split(',').map(Number);
                return {
                  latitude,
                  longitude,
                  timestamp: new Date().toISOString()
                };
              });
              
              // Calculate route distance
              const distance = trackPoints.length >= 2 ? calculateRouteDistance(trackPoints) : 0;
              
              routes.push({
                name: name || `Imported Polygon ${routes.length + 1}`,
                description: description || '',
                trackPoints,
                distance,
                color: '#10B981', // Default green color for polygons
                properties: {
                  source: 'kml',
                  type: 'polygon',
                  layer
                }
              });
            }
          }
        }
      }
    }

    logInfo(`Parsed ${points.length} points and ${routes.length} routes from KML with ${layers.size} layers`);
    return { points, routes, layers: Array.from(layers) };
  } catch (error) {
    logError('Failed to parse KML:', error);
    throw new Error('Failed to parse KML file');
  }
};